import { useState, useRef } from 'react'
import { useAuthStore, usePlayerStore } from '../stores'
import { uploadToCloudinary } from '../utils/cloudinary'
import { useProStatus } from '../utils/useProStatus'
import { FileText, Upload, CheckCircle2, X, Loader2, AlertCircle } from 'lucide-react'

interface DocItem {
  key: string
  label: string
  description: string
  url: string | null
  field: string
}

function PlayerDocuments() {
  const { user } = useAuthStore()
  const { players, updatePlayer } = usePlayerStore()
  const { isPro } = useProStatus()
  const [uploading, setUploading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const currentPlayer = players.find(p =>
    p.scout?.toLowerCase() === user?.email?.toLowerCase() ||
    p.email?.toLowerCase() === user?.email?.toLowerCase() ||
    p.name?.toLowerCase() === user?.name?.toLowerCase()
  )

  const docs: DocItem[] = [
    { key: 'cv', label: 'Football CV', description: 'Professional football resume highlighting your career', url: currentPlayer?.cv_url || null, field: 'cv_url' },
    { key: 'resume', label: 'Resume / Portfolio', description: 'General resume or portfolio document', url: currentPlayer?.resume_url || null, field: 'resume_url' },
    { key: 'passport', label: 'Passport', description: 'Valid passport for international opportunities', url: currentPlayer?.passport_url || null, field: 'passport_url' },
    { key: 'birth_cert', label: 'Birth Certificate', description: 'Official birth certificate for age verification', url: currentPlayer?.birth_certificate_url || null, field: 'birth_certificate_url' },
    { key: 'medical', label: 'Medical Report', description: 'Recent medical report or fitness certificate', url: currentPlayer?.medical_report_url || null, field: 'medical_report_url' },
    { key: 'certificates', label: 'Certificates & Awards', description: 'Coaching badges, awards, or other certificates', url: null, field: 'documents_certificates_urls' },
  ]

  const handleUpload = async (key: string, field: string) => {
    const input = fileRefs.current[key]
    if (!input?.files?.[0] || !currentPlayer?.id) return

    if (!isPro) {
      const uploadedCount = docs.filter(d => d.url).length
      if (uploadedCount >= 1) {
        setError('Free plan allows 1 document upload. Upgrade to Pro for unlimited.')
        return
      }
    }

    const file = input.files[0]
    setError(null)
    setUploading(key)

    try {
      const result = await uploadToCloudinary(file, 'scoutang/documents')
      const fileUrl = result.secure_url

      if (field === 'documents_certificates_urls') {
        const existing = currentPlayer.documents_certificates_urls || []
        await updatePlayer(currentPlayer.id, {
          documents_certificates_urls: [...existing, fileUrl],
        })
      } else {
        await updatePlayer(currentPlayer.id, { [field]: fileUrl })
      }

      input.value = ''
    } catch (err: any) {
      setError(err.message || 'Upload failed')
    } finally {
      setUploading(null)
    }
  }

  const handleRemove = async (field: string, indexToRemove?: number) => {
    if (!currentPlayer?.id) return
    try {
      if (field === 'documents_certificates_urls' && typeof indexToRemove === 'number') {
        const existing = currentPlayer.documents_certificates_urls || []
        await updatePlayer(currentPlayer.id, {
          documents_certificates_urls: existing.filter((_, i) => i !== indexToRemove),
        })
      } else {
        await updatePlayer(currentPlayer.id, { [field]: null })
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove document')
    }
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex items-center gap-2 mb-2">
        <FileText size={18} className="text-green-500" />
        <h3 className="text-xl font-bold text-white">Documents</h3>
      </div>
      <p className="text-sm text-gray-400">Upload and manage your football documents. These will be visible to scouts and clubs you connect with.</p>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
          <AlertCircle size={16} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {docs.map((doc) => {
          const isUploading = uploading === doc.key
          const isCertificates = doc.key === 'certificates'
          const certUrls = currentPlayer?.documents_certificates_urls || []
          const hasFile = isCertificates ? certUrls.length > 0 : !!doc.url

          return (
            <div key={doc.key} className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 hover:border-gray-700 transition">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-sm font-bold text-white">{doc.label}</h4>
                  <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-0.5">{doc.description}</p>
                </div>
                {hasFile && (
                  <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest bg-green-500/10 text-green-400 rounded-full">
                    <CheckCircle2 size={10} />
                    Uploaded
                  </span>
                )}
              </div>

              {isCertificates && certUrls.length > 0 && (
                <div className="space-y-2 mb-3">
                  {certUrls.map((url, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-[#0d1117] rounded-lg border border-gray-800">
                      <FileText size={14} className="text-gray-500 flex-shrink-0" />
                      <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-green-400 hover:underline truncate flex-1">
                        Certificate {i + 1}
                      </a>
                      <button
                        onClick={() => handleRemove('documents_certificates_urls', i)}
                        className="text-red-400 hover:text-red-300 flex-shrink-0"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {!isCertificates && doc.url && (
                <div className="flex items-center gap-2 p-2 bg-[#0d1117] rounded-lg border border-gray-800 mb-3">
                  <FileText size={14} className="text-gray-500 flex-shrink-0" />
                  <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-xs text-green-400 hover:underline truncate flex-1">
                    View uploaded file
                  </a>
                  <button
                    onClick={() => handleRemove(doc.field)}
                    className="text-red-400 hover:text-red-300 flex-shrink-0"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              <input
                ref={(el) => { fileRefs.current[doc.key] = el }}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={() => handleUpload(doc.key, doc.field)}
                className="hidden"
              />
              <button
                onClick={() => fileRefs.current[doc.key]?.click()}
                disabled={isUploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0d1117] border border-gray-800 rounded-xl text-sm text-gray-300 hover:text-white hover:border-green-500/30 transition disabled:opacity-50"
              >
                {isUploading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={14} />
                    {hasFile ? 'Replace File' : 'Upload File'}
                  </>
                )}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default PlayerDocuments
